import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { RefreshCw, Save } from "lucide-react";
import { billingApi, type BillingSku } from "./api";
import { ConsumoCommissionCard } from "./ConsumoCommissionCard";
import { useToast } from "@/app/providers/Toaster";
import { usePermission } from "@/lib/permissions/useCan";
import { PERMISSIONS } from "@/lib/permissions/catalog";

const FREQ_LABEL: Record<string, string> = {
  one_shot: "Único",
  per_event: "Por evento",
  monthly: "Mensual (pase 30d)",
  yearly: "Anual",
};

const clp = (n: number) => "$" + n.toLocaleString("es-CL");

export function PricingScreen() {
  const qc = useQueryClient();
  const { push } = useToast();
  const canEdit = usePermission(PERMISSIONS.BILLING_SKUS_EDIT);

  const skusQuery = useQuery({ queryKey: ["billing", "skus"], queryFn: billingApi.listSkus });
  const [draft, setDraft] = useState<Record<number, Partial<BillingSku>>>({});

  useEffect(() => setDraft({}), [skusQuery.dataUpdatedAt]);

  const save = useMutation({
    mutationFn: ({ id, body }: { id: number; body: Partial<BillingSku> }) => billingApi.updateSku(id, body),
    onSuccess: () => {
      push({ kind: "success", message: "SKU guardado." });
      qc.invalidateQueries({ queryKey: ["billing", "skus"] });
    },
    onError: () => push({ kind: "error", message: "No se pudo guardar el SKU." }),
  });

  const setField = (id: number, field: keyof BillingSku, value: unknown) =>
    setDraft((d) => ({ ...d, [id]: { ...d[id], [field]: value } }));

  const rows = skusQuery.data ?? [];

  return (
    <div className="flex flex-col gap-4 animate-fade-in">
      <header className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <p className="nx-eyebrow mb-1">Facturación · Precios</p>
          <h1 className="nx-h1">Precios & SKUs</h1>
          <p className="text-sm text-fg-muted mt-1">
            Catálogo de productos cobrables. Los precios son 100% paramétricos: editar acá se refleja
            al instante en los candados del paywall. En beta los "mensuales" se cobran como pase de 30 días.
          </p>
        </div>
        <button onClick={() => skusQuery.refetch()} className="nx-btn-secondary" disabled={skusQuery.isFetching}>
          <RefreshCw size={14} className={skusQuery.isFetching ? "animate-spin" : ""} /> Actualizar
        </button>
      </header>

      <ConsumoCommissionCard canEdit={canEdit} />

      {skusQuery.isLoading ? (
        <div className="text-sm text-fg-subtle py-10 text-center">Cargando SKUs…</div>
      ) : (
        <div className="overflow-x-auto border border-border-subtle rounded-lg">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border-subtle bg-bg-subtle text-2xs uppercase tracking-wide text-fg-subtle">
                <th className="text-left font-semibold px-3 py-2">Producto</th>
                <th className="text-left font-semibold px-3 py-2">Frecuencia</th>
                <th className="text-left font-semibold px-3 py-2">Features</th>
                <th className="text-right font-semibold px-3 py-2">Precio CLP</th>
                <th className="text-center font-semibold px-3 py-2">Activo</th>
                <th className="text-right font-semibold px-3 py-2">Acción</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((s) => {
                const d = draft[s.id] || {};
                const price = d.priceClp ?? s.priceClp;
                const active = d.isActive ?? s.isActive;
                const dirty = Object.keys(d).length > 0;
                return (
                  <tr key={s.id} className="border-b border-border-subtle last:border-0 hover:bg-bg-hover">
                    <td className="px-3 py-2">
                      <div className="font-medium text-fg">{s.name}</div>
                      <div className="text-2xs text-fg-subtle">{s.skuKey}{s.isBundle ? " · bundle" : ""}</div>
                    </td>
                    <td className="px-3 py-2 text-xs text-fg-muted">{FREQ_LABEL[s.frequency] ?? s.frequency}</td>
                    <td className="px-3 py-2">
                      <div className="flex flex-wrap gap-1 max-w-[200px]">
                        {s.featureKeys.map((fk) => (
                          <span key={fk} className="inline-flex text-2xs bg-bg-subtle border border-border-subtle text-fg-muted rounded px-1.5 py-0.5">{fk}</span>
                        ))}
                      </div>
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canEdit ? (
                        <input
                          type="number"
                          className="nx-input w-28 text-right"
                          value={price}
                          min={0}
                          onChange={(e) => setField(s.id, "priceClp", Number(e.target.value))}
                        />
                      ) : (
                        <span className="tabular-nums">{clp(s.priceClp)}</span>
                      )}
                    </td>
                    <td className="px-3 py-2 text-center">
                      <input
                        type="checkbox"
                        checked={active}
                        disabled={!canEdit}
                        onChange={(e) => setField(s.id, "isActive", e.target.checked)}
                        style={{ accentColor: "var(--nx-accent)" }}
                      />
                    </td>
                    <td className="px-3 py-2 text-right">
                      {canEdit && (
                        <button
                          className="nx-btn-primary"
                          disabled={!dirty || save.isPending}
                          onClick={() => save.mutate({ id: s.id, body: d })}
                        >
                          <Save size={13} /> Guardar
                        </button>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      <p className="text-2xs text-fg-subtle">
        Nota: la recurrencia automática (MercadoPago PreApproval) está diferida post-MVP. En beta el cobro
        es one-shot + grant manual (ver pestaña Suscripciones). El precio editado nunca lo pisa el seed.
      </p>
    </div>
  );
}
